import type { ConversationRun } from '@axilon-apps/domains-ontology-ui-state'
import { SWITCH_TO_AGENT_MODE_TOOL_NAME } from '@atbash-labs/ontology-agent-wire'

import { extractAgentModeSwitch, stripAnswerEnvelope } from './answerEnvelope'

/**
 * True when `prompt` is the Agent-mode rerun of an Ask turn that invited
 * the switch (same text, previous answer carried `[[switch-to-agent-mode]]`).
 * The follow-up still runs with that prompt; the chat UI hides the duplicate
 * user bubble and shows a "Switched to Agent mode" indicator instead.
 */
export function isAgentModeSwitchRerun(
  prompt: string,
  previousTurn: ConversationRun | undefined
): boolean {
  if (previousTurn === undefined) return false
  const current = prompt.trim()
  const previous = previousTurn.prompt.trim()
  if (current.length === 0 || current !== previous) return false
  return pastTurnInvitedAgentMode(previousTurn)
}

/**
 * True when this Ask turn already produced an Agent-mode follow-up, so
 * its Switch to Agent mode button must not render again.
 *
 * `nextPrompt` is the next past turn's prompt when history has caught up,
 * or the in-flight switch prompt immediately after the click.
 */
export function isAgentModeSwitchConsumed(
  turn: ConversationRun,
  nextPrompt: string | undefined
): boolean {
  if (nextPrompt === undefined) return false
  return isAgentModeSwitchRerun(nextPrompt, turn)
}

function pastTurnInvitedAgentMode(turn: ConversationRun): boolean {
  const answer = pastTurnAnswerText(turn)
  if (answer.length > 0 && extractAgentModeSwitch(stripAnswerEnvelope(answer)).switchToAgentMode) {
    return true
  }
  return pastTurnHasSwitchTool(turn)
}

function pastTurnHasSwitchTool(turn: ConversationRun): boolean {
  const obj =
    turn.raw_response !== null && typeof turn.raw_response === 'object'
      ? (turn.raw_response as { tool_calls?: unknown })
      : null
  if (!obj || !Array.isArray(obj.tool_calls)) return false
  return obj.tool_calls.some(
    (call) =>
      call !== null &&
      typeof call === 'object' &&
      (call as { name?: unknown }).name === SWITCH_TO_AGENT_MODE_TOOL_NAME
  )
}

function pastTurnAnswerText(turn: ConversationRun): string {
  const obj =
    turn.raw_response !== null && typeof turn.raw_response === 'object'
      ? (turn.raw_response as { final_summary?: unknown; step_results?: unknown })
      : null
  if (obj && typeof obj.final_summary === 'string' && obj.final_summary.length > 0) {
    return obj.final_summary
  }
  if (obj && Array.isArray(obj.step_results)) {
    for (const step of obj.step_results) {
      if (!step || typeof step !== 'object') continue
      const summary = (step as { summary?: unknown }).summary
      if (typeof summary === 'string' && summary.length > 0) return summary
    }
  }
  return ''
}
